import type { FC } from 'react';
import { useContext } from 'react';

import { useMutation } from '@apollo/client';
import { Button, Input, Modal } from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import { UserGroup } from 'pages/Clients/interfaces';
import {
  CREATE_USER_GROUP,
  CreateUserGroupRequest,
  CreateUserGroupResponse,
  DELETE_USER_GROUP,
  DeleteUserGroupRequest,
  DeleteUserGroupResponse,
  UPDATE_USER_GROUP,
  UpdateUserGroupRequest,
  UpdateUserGroupResponse,
} from 'schemas';

import s from './GroupModal.module.scss';
import { TGroupModalProps } from './interfaces';

export const GroupModal: FC<TGroupModalProps> = ({ group, onClose }) => {
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { t } = useContext(LocalizationContext);

  const isNewGroup = Object.keys(group).length === 0;

  const [createUserGroup] = useMutation<
    CreateUserGroupResponse,
    CreateUserGroupRequest
  >(CREATE_USER_GROUP, {
    onCompleted: onClose,
    update: (cache, { data }) => {
      const updatedGroup = data?.createUserGroup;
      if (updatedGroup) {
        cache.modify({
          fields: {
            userGroups: (groups: UserGroup[]) => groups.concat(updatedGroup),
          },
        });
      }
    },
  });

  const [updateUserGroup] = useMutation<
    UpdateUserGroupResponse,
    UpdateUserGroupRequest
  >(UPDATE_USER_GROUP, {
    onCompleted: onClose,
    update: (cache, { data }) => {
      const updatedGroup = data?.updateUserGroup;
      if (updatedGroup) {
        cache.modify({
          fields: {
            userGroups: (groups: UserGroup[], { readField }) =>
              groups.map((group) =>
                readField('uuid', group) === updatedGroup?.uuid
                  ? updatedGroup
                  : group
              ),
          },
        });
      }
    },
  });

  const [deleteUserGroup] = useMutation<
    DeleteUserGroupResponse,
    DeleteUserGroupRequest
  >(DELETE_USER_GROUP, {
    onCompleted: onClose,
    update: (cache, { data }) => {
      const deletedGroup = data?.deleteUserGroup;
      if (deletedGroup) {
        cache.modify({
          fields: {
            userGroups: (groups: UserGroup[], { readField }) =>
              groups.filter(
                (group) => readField('uuid', group) !== deletedGroup.uuid
              ),
          },
        });
      }
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<{ discount: string; title: string }>({
    mode: 'onChange',
  });

  return (
    <Modal onClose={onClose} className={s.container}>
      <div className={s.header}>
        {isNewGroup ? t('clients.group.new') : t('clients.group')}
      </div>
      <form
        className={s.form}
        onSubmit={handleSubmit(({ discount, title }) => {
          if (!permissions?.has('client_manage')) return;

          const input = { discount: Number(discount), title };

          if (isNewGroup) {
            return createUserGroup({
              variables: { input },
            });
          }
          return updateUserGroup({
            variables: { uuid: group.uuid, input },
          });
        })}
      >
        <div className={s.formItems}>
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                error={error?.message}
                title={t('accounts.group.name')}
                autoComplete="off"
                maxLength={16}
              />
            )}
            control={control}
            defaultValue={group.title || ''}
            name="title"
            rules={{
              required: t('errors.required'),
              minLength: { value: 3, message: t('symbols.min', 3) },
              maxLength: { value: 16, message: t('symbols.max', 16) },
            }}
          />
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Input
                value={value}
                onChange={(discount) => {
                  if (discount.includes('.')) {
                    return;
                  }
                  if (
                    discount === '' ||
                    (0 <= Number(discount) && Number(discount) <= 100)
                  ) {
                    onChange(discount);
                  }
                }}
                error={error?.message}
                title={`${t('discount')}, %`}
                autoComplete="off"
                errorWrapperClassName={s.discount}
              />
            )}
            control={control}
            defaultValue={String(group.discount ?? '')}
            name="discount"
            rules={{
              required: t('errors.required'),
            }}
          />
        </div>
        <div className={s.formItems}>
          {!isNewGroup && (
            <Button
              variant="outline"
              color="red"
              disabled={
                group.user_count > 0 || !permissions?.has('client_manage')
              }
              fullWidth
              onClick={() => {
                if (!permissions?.has('client_manage')) return;

                return deleteUserGroup({ variables: { uuid: group.uuid } });
              }}
            >
              {t('delete')}
            </Button>
          )}
          <Button
            type="submit"
            disabled={
              isSubmitting || !isValid || !permissions?.has('client_manage')
            }
            fullWidth
          >
            {isNewGroup ? t('create') : t('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
